import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus, Printer, ReceiptText } from 'lucide-react';
import { PERMISSIONS, SalesOrderStatus, type SalesOrderSummary } from '@optiflow/types';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { useCurrency } from '@/hooks/useCurrency';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import { useSalesOrdersQuery } from '../api/sales.api';

const PAGE_SIZE = 20;

const PAYMENT_TONES: Record<string, 'neutral' | 'warning' | 'success' | 'danger'> = {
  UNPAID: 'danger',
  PARTIALLY_PAID: 'warning',
  PAID: 'success',
  PARTIALLY_REFUNDED: 'warning',
  REFUNDED: 'neutral',
};

/** Orders taken at the counter. Deferred lens orders join this list in Phase 3B. */
export function SalesOrdersPage() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const { format } = useCurrency();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<SalesOrderStatus | ''>('');
  const [page, setPage] = useState(1);

  const deferredSearch = useDeferredValue(search);
  const { data, isPending, isError, error, refetch, isFetching } = useSalesOrdersQuery({
    page,
    pageSize: PAGE_SIZE,
    search: deferredSearch,
    status,
  });

  const columns: Column<SalesOrderSummary>[] = [
    {
      key: 'reference',
      header: t('sales.fields.reference'),
      primary: true,
      render: (order) => (
        <span className="flex flex-col">
          <span className="text-ink font-mono font-medium" dir="ltr">
            {order.reference}
          </span>
          <span className="text-muted text-xs">
            {order.customerName ?? t('sales.invoice.walkIn')} · {order.branchName}
          </span>
        </span>
      ),
    },
    {
      key: 'total',
      header: t('sales.fields.total'),
      align: 'end',
      render: (order) => (
        <span className="text-ink font-medium" dir="ltr">
          {format(order.totalMinor)}
        </span>
      ),
    },
    {
      key: 'balance',
      header: t('sales.fields.balance'),
      align: 'end',
      render: (order) =>
        // The canonical receivable counts a cancelled order as owing nothing, so no balance is claimed for it.
        order.status === 'CANCELLED' ? (
          <span className="text-muted">—</span>
        ) : (
          <span
            className={order.balanceMinor > 0 ? 'font-semibold text-amber-700' : 'text-muted'}
            dir="ltr"
          >
            {format(order.balanceMinor)}
          </span>
        ),
    },
    {
      key: 'payment',
      header: t('sales.fields.payment'),
      render: (order) =>
        // A cancelled order has no payment status; a dash rather than "Unpaid".
        order.status === 'CANCELLED' ? (
          <span className="text-muted" aria-label={t('sales.noPaymentStatus')}>
            —
          </span>
        ) : (
          <Badge tone={PAYMENT_TONES[order.paymentStatus] ?? 'neutral'}>
            {t(`sales.paymentStatuses.${order.paymentStatus}`)}
          </Badge>
        ),
    },
    {
      key: 'fulfilment',
      header: t('sales.fields.fulfilment'),
      render: (order) => (
        <span className="text-muted text-xs">
          {t(`sales.fulfilmentStatuses.${order.fulfilmentStatus}`)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (order) => (
        <div className="flex flex-wrap justify-end gap-1">
          {/* Reprints the receipt straight from the list, in the same small print window the counter and the order page open. */}
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('sales.orders.printReceiptFor', { reference: order.reference })}
            onClick={() =>
              window.open(`/print/receipt/${order.id}`, '_blank', 'noopener,width=420,height=640')
            }
          >
            <Printer className="size-4" aria-hidden />
            {t('sales.print.printReceipt')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('sales.orders.openInvoiceFor', { reference: order.reference })}
            onClick={() => navigate(`/sales/orders/${order.id}`)}
          >
            {t('sales.orders.view')}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('sales.orders.title')}
        subtitle={t('sales.orders.subtitle')}
        actions={
          can(PERMISSIONS.SALES_CREATE) ? (
            <Button onClick={() => navigate('/sales/counter')}>
              <Plus className="size-4" aria-hidden />
              {t('sales.orders.newSale')}
            </Button>
          ) : undefined
        }
      />

      <Card>
        <CardHeader
          title={t('sales.orders.title')}
          subtitle={
            data ? t('sales.orders.count', { count: data.pagination.totalItems }) : undefined
          }
          action={
            <div className="flex flex-wrap items-center gap-2">
              <div className="w-40 sm:w-52">
                <Input
                  type="search"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setPage(1);
                  }}
                  placeholder={t('sales.orders.searchPlaceholder')}
                  aria-label={t('sales.orders.searchLabel')}
                />
              </div>
              <div className="w-36">
                <Select
                  options={[
                    { value: '', label: t('sales.orders.allStatuses') },
                    ...Object.values(SalesOrderStatus).map((value) => ({
                      value,
                      label: t(`sales.statuses.${value}`),
                    })),
                  ]}
                  value={status}
                  aria-label={t('sales.fields.status')}
                  onChange={(event) => {
                    setStatus(event.target.value as SalesOrderStatus | '');
                    setPage(1);
                  }}
                />
              </div>
            </div>
          }
        />

        <DataTable
          caption={t('sales.orders.tableCaption')}
          columns={columns}
          rows={data?.items}
          rowKey={(order) => order.id}
          isLoading={isPending}
          isError={isError}
          errorMessage={apiErrorMessage(error) ?? t('sales.errors.loadOrders')}
          onRetry={() => void refetch()}
          emptyIcon={ReceiptText}
          emptyTitle={t('sales.orders.empty')}
          emptyDescription={t('sales.orders.emptyHint')}
        />

        {data ? (
          <Pagination pagination={data.pagination} onPageChange={setPage} isBusy={isFetching} />
        ) : null}
      </Card>
    </div>
  );
}
